<template>
  <div
    class="share-modal-background"
    :class="{
      'is-visible': isOpen,
      'is-present': isOpen
    }"
  ></div>
  <div
    class="share-modal"
    :class="{ 'is-open': isOpen }"
  >
    <div class="share-modal-content">
      <div class="share-modal-header">
        <span class="header-icon-and-title">
          <i class="fas fa-share"></i>&nbsp;Share MarketCarpet
        </span>
        <button
          type="button"
          title="Close"
          class="modal-close-button"
          @click="closeModal()"
        >
          <i class="fas fa-fw fa-times-circle fa-fw"></i>
        </button>
      </div>
      <div class="share-modal-body">
        <p class="info">Permalinks can be bookmarked, saved, or shared with others.</p>
        <input
          class="url-input"
          :value="url"
          readonly
        />
        <div class="btn-row">
          <button type="button" class="copy-btn" @click="copyLink()">
            <i
              class="fa"
              :class="{
                'fa-check': isLinkCopied,
                'fa-copy': !isLinkCopied
              }"
            ></i>
            <span>&nbsp;&nbsp;{{ isLinkCopied ? 'Link Copied!' : 'Copy Link' }}</span>
          </button>
          <button
            type="button"
            class="close-btn"
            @click="closeModal()"
          >Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue';

  const LINK_COPIED_ANIMATION_DURATION = 1500;

  const props = defineProps<{
    isOpen: boolean;
  }>();

  const emit = defineEmits<{
    (event: 'close'): void;
  }>();

  const url = ref(globalThis.location.href);
  const isLinkCopied = ref(false);

  watch(() => props.isOpen, (open) => {
    if (open) {
      url.value = globalThis.location.href;
      isLinkCopied.value = false;
    }
  });

  function closeModal() {
    emit('close');
  }

  function copyLink() {
    navigator.clipboard.writeText(globalThis.location.href);
    isLinkCopied.value = true;

    setTimeout(() => {
      isLinkCopied.value = false;
    }, LINK_COPIED_ANIMATION_DURATION);
  }
</script>

<style scoped>
  .share-modal {
    position: fixed;
    top: 0px;
    right: 0px;
    left: 0px;
    bottom: 0px;
    z-index: 1000;
    pointer-events: none;
    opacity: 0.0;
    transition: opacity 175ms ease;
    color: #212529;
    font-size: 16px;
    font-weight: 400;
    user-select: none;
    line-height: 1.5;
    text-align: left;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  .share-modal.is-open {
    pointer-events: all;
    opacity: 1.0;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .share-modal-background {
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    z-index: 1000;
    background-color: #333;
    opacity: 0.0;
    transition: opacity 150ms linear;
    display: none;
  }

  .share-modal-background.is-present {
    display: block;
  }

  .share-modal-background.is-visible {
    opacity: 0.5;
  }

  .share-modal-content {
    position: relative;
    margin: 65px auto 30px auto;
    max-width: 525px;
    transform: translate(0, -25%);
    overflow: visible;
    border-radius: 6px;
    background-color: white;
    box-shadow: 0px 5px 15px rgb(0 0 0 / 50%);
    transition: transform 300ms ease-out;
  }

  .share-modal.is-open .share-modal-content {
    transform: translate(0, 0);
  }

  .share-modal-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 45px;
    box-sizing: border-box;
    font-family: 'Roboto Condensed', 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 900;
    font-size: 20px;
    line-height: 1;
    padding: 10px;
    vertical-align: middle;
    background-color: #434c56;
    color: white;
    border-radius: 6px 6px 0px 0px;
  }

  .share-modal-header::before,
  .share-modal-header::after {
    all: unset;
  }

  .header-icon-and-title {
    vertical-align: middle;
  }

  .modal-close-button {
    color: #a9b2bc;
    font-size: 20px;
    font-weight: 900;
    background: inherit;
    border: none;
    cursor: pointer;
  }

  .modal-close-button:hover {
    color: white;
  }

  .share-modal-body {
    overflow: visible;
    border-radius: 0 0 6px 6px;
    display: grid;
    gap: 10px;
    padding: 15px;
    text-align: center;
  }

  .info {
    margin: 0 auto 15px auto;
    line-height: 1.40;
    font-size: 16px;
    font-weight: normal;
    color: #5a6672;
  }

  .url-input {
    height: 45px;
    font-size: 16px;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    width: 100%;
    margin: 0px auto;
    padding-left: 10px;
    padding-right: 10px;
    outline: none;
    box-shadow: none;
    box-sizing: border-box;
    background-color: #fff;
    border: 2px solid #c0ced8;
    border-radius: 5px;
    font-weight: bold;
    color: #5a6672;
    margin-bottom: 10px;
  }

  .url-input:hover,
  .url-input:active,
  .url-input:focus {
    border-color: #707f8f;
    font-weight: bold;
    color: #2d3339;
  }

  .btn-row {
    margin: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 10px;
    border-top: 1px solid #c0ced8;
    gap: 10px;
  }

  .copy-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.42857143;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    border: 2px solid #1a7a47;
    border-radius: 5px;
    background: #1a7a47;
    color: #fff;
    cursor: pointer;
    outline: none;
    box-shadow: none;
    box-sizing: border-box;
    transition: all 0.35s ease;
  }

  .copy-btn:hover,
  .copy-btn:active,
  .copy-btn:focus {
    background: #1a7a47;
    border-color: #1a7a47;
    color: #fff;
  }

  .close-btn {
    color: #0f4d8a;
    background: none;
    border: none;
    margin: 0px;
    padding: 0px;
    font-size: 14px;
    font-weight: bold;
    font-family: 'Lato', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    cursor: pointer;
  }

  .close-btn:hover {
    filter: brightness(1.5);
  }
</style>
